import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Icon } from "@/components/Icon";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useChrome } from "../ui/MobileShell";
import wordmark from "@/assets/pnrbet-wordmark.png";

/* MobileRegister (design 8b) — three short steps, not one long form. Age and
   state are asked up front so ineligible users learn before investing effort.

   Mapped to the REAL backend contract (the only account-creation path there is):
     Step 1 (designed step 1) — email + password + DOB + STATE + 18+/terms.
        • Age gate is enforced client-side (18+).
        • State eligibility is REAL: GET /geo/check tells us the detected state
          and whether the location is allowed; an excluded location is surfaced
          before any effort. (The user-typed state is a courtesy; the geo check is
          authoritative.)
        • On Continue → POST /auth/email/request-code (sends the 6-digit code).
     Step 2 — the 6-digit email code → POST /auth/email/verify-code.
     Step 3 — choose a username → POST /auth/register → AuthContext.login.

   Steps 2–3 implement the backend's required email-verification + username — this
   is the documented "implementation decision" for the design's steps 2–3. All
   text fields use the NORMAL OS keyboard + autofill (the code field is a numeric
   one-time-code input, never the custom stake keypad). Full-height bare chrome. */

const GREEN = "#00D632";

// US states for the eligibility select (the geo check is authoritative; this is
// a courtesy field so the user states their residence up front).
const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

function pwStrength(pw) {
  let s = 0;
  if (pw.length >= 8) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return Math.min(4, s);
}

export default function MobileRegister() {
  useChrome({ bare: true });
  const navigate = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState(1); // 1 details · 2 code · 3 username
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [dob, setDob] = useState("");
  const [stateCode, setStateCode] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [code, setCode] = useState("");
  const [username, setUsername] = useState("");
  const [geoBlocked, setGeoBlocked] = useState(null); // { stateName } when excluded
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const strength = pwStrength(password);
  const maxDob = new Date(Date.now() - 18 * 31557600000).toISOString().split("T")[0];

  const startStep1 = async () => {
    setErr("");
    if (!email) { setErr("Enter your email."); return; }
    if (password.length < 6) { setErr("Password must be at least 6 characters."); return; }
    if (!dob) { setErr("Enter your date of birth."); return; }
    const age = Math.floor((Date.now() - new Date(dob).getTime()) / 31557600000);
    if (age < 18) { setErr("You must be 18 or older to register."); return; }
    if (!stateCode) { setErr("Select your state."); return; }
    if (!agreed) { setErr("Please agree to the Terms and Sweepstakes Rules."); return; }
    setBusy(true);
    try {
      // Authoritative eligibility: check the detected location before sending a code.
      try {
        const { data: geo } = await api.get("/geo/check");
        if (geo && geo.allowed === false) { setGeoBlocked({ stateName: geo.state_name || "your location" }); setBusy(false); return; }
      } catch { /* a geo outage is not a block — the server re-checks at register */ }
      await api.post("/auth/email/request-code", { email });
      setStep(2);
    } catch (e) {
      setErr(apiError(e?.response?.data?.detail) || "Could not start sign-up. Please try again.");
    } finally { setBusy(false); }
  };

  const verifyStep2 = async () => {
    setErr("");
    if (code.trim().length < 6) { setErr("Enter the 6-digit code from your email."); return; }
    setBusy(true);
    try {
      await api.post("/auth/email/verify-code", { email, code: code.trim() });
      setStep(3);
    } catch (e) {
      setErr(apiError(e?.response?.data?.detail) || "That code didn't match. Try again.");
    } finally { setBusy(false); }
  };

  const finishStep3 = async () => {
    setErr("");
    if (!username.trim()) { setErr("Choose a username."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", {
        username: username.trim(), email, password, dob, agreed_terms: true, referral_code: null,
      });
      login(data.token, data.user);
      navigate("/", { replace: true });
    } catch (e) {
      setErr(apiError(e?.response?.data?.detail) || "Could not create your account.");
    } finally { setBusy(false); }
  };

  if (geoBlocked) {
    return (
      <Shell step={1}>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "0 10px" }}>
          <span style={{ width: 64, height: 64, borderRadius: "50%", background: "rgba(239,68,68,.12)", boxShadow: "inset 0 0 0 1.5px #EF4444", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon name="shield" size={28} style={{ color: "#EF4444" }} />
          </span>
          <div style={{ marginTop: 16, font: "800 24px 'Manrope'", letterSpacing: "-.02em", color: "#fff" }}>Not available in {geoBlocked.stateName}</div>
          <p style={{ marginTop: 8, font: "500 14px/1.6 'Outfit'", color: "#a1a1aa" }}>PRYMEBET isn't available in your location yet. We check this up front so you don't waste time setting up an account you can't use.</p>
          <Link to="/signin" style={{ marginTop: 20, font: "700 15px 'Outfit'", color: GREEN, textDecoration: "none" }}>Back to sign in</Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell step={step}>
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column" }}>
        {step === 1 && (
          <>
            <div style={{ font: "800 28px 'Manrope'", letterSpacing: "-.02em", color: "#fff" }}>Let's get you in</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 20 }}>
              <Field label="Email">
                <input type="email" inputMode="email" autoComplete="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} enterKeyHint="next"
                  value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" style={inputStyle} />
              </Field>
              <Field label="Password">
                <input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" style={inputStyle} />
              </Field>
              {password.length > 0 && (
                <div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 5 }}>
                    {[0, 1, 2, 3].map((i) => <span key={i} style={{ height: 4, borderRadius: 999, background: i < strength ? GREEN : "#26262B" }} />)}
                  </div>
                  <div style={{ marginTop: 6, font: "500 12px 'Outfit'", color: "#a1a1aa" }}>
                    {["Too short", "Weak", "Okay", "Strong", "Strong · add a symbol to max it out"][strength]}
                  </div>
                </div>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <Field label="Date of birth">
                  <input type="date" max={maxDob} value={dob} onChange={(e) => setDob(e.target.value)} style={{ ...inputStyle, colorScheme: "dark" }} />
                </Field>
                <Field label="State">
                  <select value={stateCode} onChange={(e) => setStateCode(e.target.value)} style={{ ...inputStyle, color: stateCode ? "#fff" : "#52525b" }}>
                    <option value="">Select</option>
                    {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
              </div>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 10, marginTop: 4, cursor: "pointer" }}>
                <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} style={{ width: 22, height: 22, flex: "none", accentColor: GREEN, marginTop: 1 }} />
                <span style={{ font: "500 13px/1.5 'Outfit'", color: "#a1a1aa" }}>
                  I'm 18 or older and agree to the <Link to="/terms" style={linkStyle}>Terms</Link> and <Link to="/sweeps-rules" style={linkStyle}>Sweepstakes Rules</Link>.
                </span>
              </label>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div style={{ font: "800 28px 'Manrope'", letterSpacing: "-.02em", color: "#fff" }}>Check your email</div>
            <p style={{ marginTop: 8, font: "500 14px 'Outfit'", color: "#a1a1aa" }}>We sent a 6-digit code to <span style={{ color: "#fff" }}>{email}</span>.</p>
            <div style={{ marginTop: 20 }}>
              <Field label="Verification code">
                <input inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*" maxLength={6} enterKeyHint="go"
                  value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="______"
                  style={{ ...inputStyle, textAlign: "center", letterSpacing: ".5em", fontVariantNumeric: "tabular-nums" }} />
              </Field>
              <button type="button" onClick={startStep1} disabled={busy} style={{ marginTop: 12, background: "none", border: 0, cursor: "pointer", font: "600 13px 'Outfit'", color: GREEN }}>Resend code</button>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div style={{ font: "800 28px 'Manrope'", letterSpacing: "-.02em", color: "#fff" }}>Pick a username</div>
            <p style={{ marginTop: 8, font: "500 14px 'Outfit'", color: "#a1a1aa" }}>This is how other players will see you.</p>
            <div style={{ marginTop: 20 }}>
              <Field label="Username">
                <input autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} enterKeyHint="go"
                  value={username} onChange={(e) => setUsername(e.target.value)} placeholder="johndoe" style={inputStyle} />
              </Field>
            </div>
          </>
        )}

        <div style={{ flex: 1 }} />
        {step === 1 && (
          <p style={{ font: "500 12px/1.5 'Outfit'", color: "#71717a", marginTop: 16 }}>Not available in excluded states. We'll check your location next.</p>
        )}
        {err && <div role="alert" style={{ marginTop: 12, font: "600 13px 'Outfit'", color: "#EF4444" }}>{err}</div>}
      </div>

      <div style={{ paddingTop: 14 }}>
        <button type="button" onClick={step === 1 ? startStep1 : step === 2 ? verifyStep2 : finishStep3} disabled={busy || (step === 1 && !agreed)}
          style={{ width: "100%", height: 56, borderRadius: 16, background: GREEN, color: "#0A0A0A", font: "800 16px 'Manrope'", border: 0, cursor: busy ? "default" : "pointer", opacity: busy || (step === 1 && !agreed) ? 0.5 : 1, boxShadow: "inset 0 1px 0 rgba(255,255,255,.3)" }}>
          {busy ? "Please wait…" : step === 1 ? "Continue" : step === 2 ? "Verify & continue" : "Create account"}
        </button>
        {step === 1 && (
          <p style={{ textAlign: "center", font: "500 14px 'Outfit'", color: "#a1a1aa", marginTop: 16 }}>
            Have an account?{" "}
            <Link to="/signin" style={{ font: "700 14px 'Outfit'", color: GREEN, textDecoration: "none" }}>Sign in</Link>
          </p>
        )}
      </div>
    </Shell>
  );
}

function Shell({ step, children }) {
  const navigate = useNavigate();
  return (
    <div className="m-app" style={{ position: "fixed", inset: 0, zIndex: 50, background: "#0A0A0A", display: "flex", flexDirection: "column", paddingTop: "var(--m-safe-top)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 12px 0" }}>
        <button type="button" aria-label="Back" onClick={() => (step > 1 ? window.history.back() : navigate("/signin"))}
          style={{ width: 40, height: 40, flex: "none", background: "none", border: 0, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon name="chevron-left" size={24} className="text-white" />
        </button>
        <span style={{ flex: 1, textAlign: "center", font: "800 18px 'Manrope'", color: "#fff" }}>Create account</span>
        <span style={{ width: 40, flex: "none", textAlign: "right", font: "600 12px 'Outfit'", color: "#71717a" }}>{step} of 3</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 6, padding: "12px 16px 6px" }}>
        {[1, 2, 3].map((i) => <span key={i} style={{ height: 4, borderRadius: 999, background: i <= step ? GREEN : "#26262B" }} />)}
      </div>
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", padding: "8px 16px calc(24px + var(--m-safe-bot))" }}>
        <img src={wordmark} alt="PRYMEBET" style={{ height: 24, width: "auto", display: "block", marginBottom: 14 }} />
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label style={{ display: "block", font: "600 11px 'Outfit'", letterSpacing: ".04em", color: "#a1a1aa", marginBottom: 7 }}>{label}</label>
      <div style={{ display: "flex", alignItems: "center", height: 54, boxSizing: "border-box", padding: "0 14px", borderRadius: 14, background: "#111114", boxShadow: "inset 0 0 0 1px #26262B" }}>
        {children}
      </div>
    </div>
  );
}

const inputStyle = { flex: 1, minWidth: 0, height: "100%", background: "transparent", border: 0, outline: "none", color: "#fff", font: "600 16px 'Outfit'" };
const linkStyle = { color: GREEN, textDecoration: "none", fontWeight: 600 };
